import type {
  BalanceItem,
  BalanceSubscription,
  TransactionRow,
  VbInvoice,
  VossSentInvoice,
} from './CustomerTransactions.types';

const earliestStartDate = (items: BalanceItem[]): string | undefined => {
  const dates = items
    .map((item) => item.period?.startDate)
    .filter((date): date is string => Boolean(date));

  if (dates.length === 0) {
    return undefined;
  }

  return dates.reduce((earliest, current) => (current < earliest ? current : earliest));
};

export const mergeTransactions = (
  vbInvoices: VbInvoice[],
  vossSent: VossSentInvoice[],
  balanceSubscriptions: BalanceSubscription[]
): TransactionRow[] => {
  const rows: TransactionRow[] = [];
  const matchedVossIds = new Set<string>();

  const vossByInvoiceNumber = new Map<string, VossSentInvoice>();
  vossSent.forEach((voss) => {
    if (voss.invoiceNumber !== undefined && voss.invoiceNumber !== '') {
      vossByInvoiceNumber.set(voss.invoiceNumber, voss);
    }
  });

  vbInvoices.forEach((vb) => {
    const hasFile = Boolean(vb.filePath);
    const invoiceNumber = vb.invoiceNumber ?? '';
    const hasInvoiceNumber = invoiceNumber !== '';
    const match = hasInvoiceNumber ? vossByInvoiceNumber.get(invoiceNumber) : undefined;
    if (match) {
      matchedVossIds.add(match.id);
    }

    rows.push({
      id: `vb-${vb.id}`,
      type: match ? 'vbVossMatched' : 'vbOnly',
      description: undefined,
      invoiceNumber: vb.invoiceNumber,
      date: vb.invoiceDate,
      amount: vb.amount,
      residual: vb.balance,
      status: vb.status,
      posted: vb.paymentType,
      isUninvoiced: !hasFile && !hasInvoiceNumber,
      hasPdf: hasFile && vb.deleted !== true,
      vossId: match?.id,
      vbId: vb.id,
    });
  });

  vossSent
    .filter((voss) => !matchedVossIds.has(voss.id))
    .forEach((voss) => {
      rows.push({
        id: `voss-${voss.id}`,
        type: 'vossOnlySent',
        description: undefined,
        invoiceNumber: voss.invoiceNumber,
        date: voss.invoiceDate,
        amount: voss.totalAmount,
        residual: voss.unpaidAmount ?? 0,
        status: undefined,
        posted: undefined,
        isUninvoiced: false,
        hasPdf: false,
        vossId: voss.id,
      });
    });

  balanceSubscriptions.forEach((sub) => {
    rows.push({
      id: `sub-${sub.id}`,
      type: 'uninvoicedSub',
      description: sub.name,
      invoiceNumber: undefined,
      date: earliestStartDate(sub.items),
      amount: sub.totalAmount,
      residual: sub.totalAmount,
      status: undefined,
      posted: undefined,
      isUninvoiced: true,
      hasPdf: false,
      vossId: sub.id,
    });
  });

  return rows;
};
